/**
 * @flow
 * @format
 */
/* eslint-disable react/no-array-index-key */

import React from 'react';
import { connect, type Connector } from 'react-redux';
import { Card, Row, Col } from 'reactstrap';
import _ from 'lodash';
import cc from 'classcat';

import { replaceAll, createHTMLMarkup } from '~/utils';
import { getSnubberStatus } from '~/selectors/design';

// import type { ChartType } from '~/reducers/global';
import type { ChartInfoType, ChartDataType } from '~/modules/design/types';

// import ReportInputParams from '../ReportInputParams';
// import ReportOutputParams from '../ReportOutputParams';
import ChartLineGraph from '../ChartLineGraph';
import PieChart from '../PieChart';

import classes from './AnalysisWrapper.scss';

type OwnPropsType = {};

type EnhancedProps = {
  charts: Array<{ data: ChartDataType, labels: ChartInfoType, name: string }>,
  effSummary: Array<*>,
  hasSnubber: boolean,
};

type PropsType = OwnPropsType & EnhancedProps;

function replaceTableText(str: string) {
  const obj = {
    // min: 'Minimum',
    // nom: 'Nominal',
    // max: 'Maximim',
    Vin: 'V<sub>IN</sub>', // eslint-disable-line no-useless-escape
  };
  return createHTMLMarkup(replaceAll(str, obj));
}

export class AnalysisWrapper extends React.PureComponent<PropsType, *> {
  static defaultProps = {
    effSummary: [],
    charts: {},
    hasSnubber: false,
  };

  props: PropsType; // eslint-disable-line react/sort-comp

  // componentDidMount() {
  // Adjust the graphs and legends after the components have been generated.
  // There might be an easier way to do this?
  // const groups = d3.selectAll('.graph');
  // groups.each(function each() {
  //   let viewBox: string = this.getAttribute('viewBox');
  //   const legend = d3
  //     .select(this)
  //     .select('.legend')
  //     .select('g');

  //   const legendGroup: Element = legend.node();

  //   const width = Math.round(legendGroup.getBoundingClientRect().width + 5);
  //   // legendGroup.previousElementSibling.setAttribute('width', width);

  //   // IE11: parentElement is undefined for SVG elements, use parentNode.
  //   const wrapper = legendGroup.parentNode;
  //   if (wrapper instanceof Element) {
  //     wrapper.setAttribute(
  //       'transform',
  //       `translate(-${40 + 10 + 10 + width}, 5)`,
  //     );

  //     // Move the viewbox
  //     viewBox = viewBox.replace('0', `-${width / 2}`);
  //     this.setAttribute('viewBox', viewBox);
  //   }
  // });
  // }

  render() {
    const pieCharts = _.slice(this.props.charts, 10);
    // const pieCharts = [];
    const lossCharts = _.slice(this.props.charts, 0, 10);
    // const lossCharts = _.slice(this.props.charts, 0, 1);

    // console.log(charts);
    const graphs = _.map(lossCharts, (d, k) => {
      const title = (
        <h5 style={{ textAlign: 'center' }} className={classes.cardSubHeader}>
          {replaceTableText(d.name)}
        </h5>
      );
      return (
        <Col sm={12} md={12} lg={6} key={k} className="pb-0">
          {title}
          <div className="px-xl-5">
            <div className="p-sm-1 p-md-2 p-lg-3 px-xl-5 py-xl-3">
              <div className="mx-auto" style={{ maxWidth: '700px' }}>
                <ChartLineGraph
                  graphNum={k + 1}
                  chartInfo={d.labels}
                  chartData={d.data}
                  legend="horizontal"
                  viewBox={{ x: 0, y: 0, width: 550, height: 325 }}
                />
              </div>
            </div>
          </div>
        </Col>
      );
    });

    const pieGraphs = _.map(pieCharts, d => {
      const omitFields: string[] = this.props.hasSnubber
        ? ['totalpowerloss']
        : ['totalpowerloss', 'snubber'];
      const data = _.omit(d.data, omitFields);
      const title = (
        <h5 style={{ textAlign: 'center' }} className={classes.cardSubHeader}>
          {replaceTableText(d.name)}
        </h5>
      );
      return (
        <Col xs={4} key={d.name} className="pb-5">
          <div className="pb-3">
            {title}
            <PieChart
              data={data}
              totalLoss={_.get(d, ['data', 'totalpowerloss'], 0)}
              width="100%"
              height={230}
              radius={100}
              viewBox={{
                x: 0,
                y: 0,
                width: 325,
                height: 250,
              }}
            />
          </div>
        </Col>
      );
    });

    type EffSummaryType = {
      vin: string,
      load100p: string,
      load75p: string,
      load50p: string,
      load25p: string,
    };

    const effRows: Array<React$Element<*>> = this.props.effSummary.map(
      (d: EffSummaryType) => (
        <tr key={d.vin}>
          <td>{replaceTableText(d.vin)}</td>
          <td>{d.load100p}</td>
          <td>{d.load75p}</td>
          <td>{d.load50p}</td>
          <td>{d.load25p}</td>
        </tr>
      ),
    );

    return (
      <Row>
        <h4 className={cc(['col col-sm-12', classes.cardHeader])}>
          Loss Review
        </h4>
        <Col>
          <Card body>
            <Row noGutters>
              <Col xs={12}>
                <h4 className={classes.cardSubHeader}>
                  6a: MOSFET & Diode Loss Breakdown
                </h4>
              </Col>
              {_.slice(graphs, 0, 6)}
            </Row>
            <Row noGutters>
              <Col xs={12}>
                <h4 className={classes.cardSubHeader}>
                  6b: Powertrain Component Loss Summary
                </h4>
              </Col>
              {pieGraphs}
            </Row>
            <Row noGutters>
              <Col xs={12}>
                <h4 className={classes.cardSubHeader}>
                  6c: Loss & Efficiency vs. V<sub>in</sub> & Load
                </h4>
              </Col>
              {_.slice(graphs, 6)}
            </Row>
            <Row>
              <Col sm={12}>
                <h5 className={cc(['text-center', classes.cardSubHeader])}>
                  Powertrain Efficiency Summary
                </h5>
              </Col>
              <Col style={{ display: 'flex', justifyContent: 'center' }}>
                <table
                  className={cc([
                    'table table-sm table-bordered',
                    classes.summaryTable,
                  ])}
                >
                  <thead className="thead-inverse">
                    <tr>
                      <th>&nbsp;</th>
                      <th>100% Load</th>
                      <th>75% Load</th>
                      <th>50% Load</th>
                      <th>25% Load</th>
                    </tr>
                  </thead>
                  <tbody>{effRows}</tbody>
                </table>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    );
  }
}

function mapStateToProps(state: AppStateType): EnhancedProps {
  const hasSnubber: boolean = getSnubberStatus(state);

  return {
    ...state.global.chartData.data,
    hasSnubber,
  };
}

const connector: Connector<OwnPropsType, PropsType> = connect(mapStateToProps);

export default connector(AnalysisWrapper);



// WEBPACK FOOTER //
// ./src/components/AnalysisWrapper/AnalysisWrapper.js